import React, { createContext, useState, useCallback, ReactNode } from "react";
import api from "../../api/api";
import { ApiResponse, PaginationInfo } from "../../types/api";

interface Quotation {
  id?: number;
  quotation_no?: string;
  quotationNo?: string;
  customer_id?: number;
  customer?: {
    id?: number;
    customer_name?: string;
    customerName?: string;
    contact_person?: string;
    contactPerson?: string;
    phone_number?: string;
    phoneNumber?: string;
    email?: string;
    address?: string;
    taxNo?: string;
    stateCode?: string;
  };
  total_amount?: string | number;
  totalAmount?: string | number;
  status?: string;
  items?: any[];
  notes?: string;
  quotation_date?: string;
  quotationDate?: string;
  valid_until?: string;
  validUntil?: string;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
  [key: string]: any;
}

interface QuotationsContextType {
  quotations: Quotation[];
  pagination: PaginationInfo | null;
  loading: boolean;
  error: string | null;
  fetchQuotations: (page?: number, perPage?: number) => Promise<void>;
  addQuotation: (quotationDetails: any) => Promise<void>;
  editQuotation: (quotationDetails: any) => Promise<void>;
  deleteQuotation: (id: number) => Promise<void>;
  convertToProject: (id: number, projectData: any) => Promise<any>;
}

export const QuotationsContext = createContext<QuotationsContextType | undefined>(undefined);

interface QuotationsProviderProps {
  children: ReactNode;
}

export const QuotationsProvider: React.FC<QuotationsProviderProps> = ({ children }) => {
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const normalizeQuotationPayload = (quotationDetails: any) => {
    const items = Array.isArray(quotationDetails.items) ? quotationDetails.items : [];
    const totalAmount = items.reduce((sum: number, item: any) => {
      const quantity = Number(item.quantity ?? 1);
      const unitPrice = Number(item.unitPrice ?? item.unit_price ?? 0);
      const itemTotal = item.total !== undefined && item.total !== null ? Number(item.total) : quantity * unitPrice;
      return sum + Number(itemTotal || 0);
    }, 0);

    const rawCustomerId = quotationDetails.customer_id || quotationDetails.customerId;
    const customerId = rawCustomerId ? Number(rawCustomerId) : null;

    return {
      quotation_no: quotationDetails.quotation_no || quotationDetails.quotationNo || quotationDetails.subject || `QT-${Date.now()}`,
      customer_id: customerId,
      total_amount: Number(quotationDetails.total_amount || quotationDetails.totalAmount || totalAmount),
      status: quotationDetails.status || "Pending",
      quotation_date: quotationDetails.quotation_date || quotationDetails.quotationDate,
      valid_until: quotationDetails.valid_until || quotationDetails.expiryDate || quotationDetails.validUntil || null,
      items: items.map((item: any) => ({
        ...item,
        quantity: Number(item.quantity ?? 1),
        unit_price: Number(item.unitPrice ?? item.unit_price ?? 0),
        total: Number(item.total ?? (Number(item.quantity ?? 1) * Number(item.unitPrice ?? item.unit_price ?? 0))),
      })),
      notes: quotationDetails.notes || quotationDetails.quotationDescription || "",
    };
  };

  // Fetch all quotations with pagination
  const fetchQuotations = useCallback(async (page: number = 1, perPage: number = 15): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get<any>(`/quotations?${params.toString()}`);
      
      // Handle Laravel pagination structure: { success: true, data: { data: [...], current_page, last_page, per_page, total, ... } }
      let quotationsData: Quotation[] = [];
      let paginationData: PaginationInfo | null = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          // Laravel pagination structure: pagination metadata is directly in response.data
          quotationsData = responseData.data.data;
          
          // Extract pagination from Laravel pagination structure (current_page, last_page, per_page, total are at data level)
          if (responseData.data.current_page !== undefined) {
            paginationData = {
              current_page: responseData.data.current_page,
              last_page: responseData.data.last_page,
              per_page: responseData.data.per_page,
              total: responseData.data.total,
            };
          } else if (responseData.data.pagination) {
            // Fallback: check if pagination is nested
            paginationData = responseData.data.pagination;
          } else if (responseData.pagination) {
            paginationData = responseData.pagination;
          }
        } else if (responseData.data && Array.isArray(responseData.data)) {
          // Check if response.data itself is an array or has pagination
          if (typeof responseData.data === 'object' && 'current_page' in responseData.data) {
            // Laravel pagination: response.data has current_page, data, etc.
            const paginatedData = responseData.data as any;
            quotationsData = Array.isArray(paginatedData.data) ? paginatedData.data : [];
            paginationData = {
              current_page: paginatedData.current_page,
              last_page: paginatedData.last_page,
              per_page: paginatedData.per_page,
              total: paginatedData.total,
            };
          } else {
            quotationsData = responseData.data;
            paginationData = responseData.pagination;
          }
        } else if (Array.isArray(responseData)) {
          quotationsData = responseData;
        }
      } else if (responseData?.data) {
        // Handle case where success might not be present
        if (responseData.data.data && Array.isArray(responseData.data.data)) {
          quotationsData = responseData.data.data;
          // Check for Laravel pagination structure
          if (responseData.data.current_page !== undefined) {
            paginationData = {
              current_page: responseData.data.current_page,
              last_page: responseData.data.last_page,
              per_page: responseData.data.per_page,
              total: responseData.data.total,
            };
          } else if (responseData.data.pagination) {
            paginationData = responseData.data.pagination;
          }
        } else if (Array.isArray(responseData.data)) {
          quotationsData = responseData.data;
        }
      } else if (Array.isArray(responseData)) {
        quotationsData = responseData;
      }
      
      setQuotations(quotationsData);
      setPagination(paginationData);
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || "Failed to fetch quotations";
      setError(errorMsg);
      console.error("Failed to fetch quotations:", err);
      setQuotations([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new quotation
  const addQuotation = useCallback(async (quotationDetails: any): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post<ApiResponse<Quotation>>("/quotations", normalizeQuotationPayload(quotationDetails));
      const newQuotation = response.data.data || response.data;
      if (newQuotation) {
        setQuotations((prevQuotations) => [...prevQuotations, newQuotation]);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || "Failed to add quotation";
      setError(errorMsg);
      console.error("Failed to add quotation:", err);
      throw err; // Re-throw to allow component to handle error
    } finally {
      setLoading(false);
    }
  }, []);

  // Edit an existing quotation
  const editQuotation = useCallback(async (quotationDetails: any): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.put<ApiResponse<Quotation>>(`/quotations/${quotationDetails.id}`, normalizeQuotationPayload(quotationDetails));
      const updatedQuotation = response.data.data || response.data;
      if (updatedQuotation) {
        setQuotations((prevQuotations) =>
          prevQuotations.map((item) =>
            item.id === quotationDetails.id ? updatedQuotation : item
          )
        );
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || "Failed to edit quotation";
      setError(errorMsg);
      console.error("Failed to edit quotation:", err);
      throw err; // Re-throw to allow component to handle error
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a quotation
  const deleteQuotation = useCallback(async (id: number): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/quotations/${id}`);
      setQuotations((prevQuotations) => prevQuotations.filter((quotation) => quotation.id !== id));
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || "Failed to delete quotation";
      setError(errorMsg);
      console.error("Failed to delete quotation:", err);
      throw err; // Re-throw to allow component to handle error
    } finally {
      setLoading(false);
    }
  }, []);

  // Convert quotation to project
  const convertToProject = useCallback(async (id: number, projectData: any): Promise<any> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post<ApiResponse<any>>(`/quotations/${id}/convert-to-project`, projectData);
      const project = response.data.data || response.data;
      
      // Update quotation status to "Converted"
      setQuotations((prevQuotations) =>
        prevQuotations.map((item) =>
          item.id === id ? { ...item, status: "Converted" } : item
        )
      );
      
      return project;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || "Failed to convert quotation to project";
      setError(errorMsg);
      console.error("Failed to convert quotation to project:", err);
      throw err; // Re-throw to allow component to handle error
    } finally {
      setLoading(false);
    }
  }, []);

  const contextValue: QuotationsContextType = {
    quotations,
    pagination,
    loading,
    error,
    fetchQuotations,
    addQuotation,
    editQuotation,
    deleteQuotation,
    convertToProject,
  };

  return (
    <QuotationsContext.Provider value={contextValue}>
      {children}
    </QuotationsContext.Provider>
  );
};

export default QuotationsProvider;
